import React from "react";
import {
  MDBBtn,
  MDBIcon,
  MDBBadge,
  MDBSpinner,
} from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import ZoneEditorModal from "./ZoneEditorModal";

/**
 * VenueEditor — editor visual de la cuadrícula de una sala.
 *
 * Props:
 *  - venue: VenueDTO (con zones y seats)
 *  - onVenueChanged: () => void — recargar el padre
 */
class VenueEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      venue: props.venue || null,
      loading: false,
      // selección de celdas para crear zona
      selecting: false,
      selStart: null, // {row, col}
      selEnd: null,
      // modales
      zoneModalOpen: false,
      zoneModalData: null, // null = nueva, objeto = editar
      zoneModalSelStart: null,
      zoneModalSelEnd: null,
      confirmDeleteZone: null,
    };
    this.gridRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.venue !== this.props.venue) {
      this.setState({ venue: this.props.venue });
    }
  }

  // ─── helpers ──────────────────────────────────────────────────────────────

  /**
   * Devuelve la zona a la que pertenece la celda (row, col), o null.
   */
  findZoneForCell = (row, col) => {
    const { venue } = this.state;
    if (!venue || !Array.isArray(venue.zones)) return null;
    return venue.zones.find(
      (z) => row >= z.rowStart && row <= z.rowEnd && col >= z.colStart && col <= z.colEnd
    ) || null;
  };

  /**
   * Devuelve la butaca para (row, col) dentro de la zona dada, o null.
   */
  findSeat = (row, col, zone) => {
    if (!zone || !Array.isArray(zone.seats)) return null;
    return zone.seats.find((s) => s.rowIdx === row && s.colIdx === col) || null;
  };

  hexToRgba = (hex, alpha) => {
    if (!hex || !hex.startsWith("#")) return `rgba(200,200,200,${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  isInSelection = (row, col) => {
    const { selecting, selStart, selEnd } = this.state;
    if (!selecting || !selStart || !selEnd) return false;
    const r0 = Math.min(selStart.row, selEnd.row);
    const r1 = Math.max(selStart.row, selEnd.row);
    const c0 = Math.min(selStart.col, selEnd.col);
    const c1 = Math.max(selStart.col, selEnd.col);
    return row >= r0 && row <= r1 && col >= c0 && col <= c1;
  };

  // ─── cell interaction ─────────────────────────────────────────────────────

  handleCellMouseDown = (row, col, e) => {
    e.preventDefault();
    this.setState({ selecting: true, selStart: { row, col }, selEnd: { row, col } });
  };

  handleCellMouseEnter = (row, col) => {
    if (this.state.selecting) {
      this.setState({ selEnd: { row, col } });
    }
  };

  handleCellMouseUp = (row, col) => {
    const { selecting, selStart } = this.state;
    if (!selecting) return;
    const selEnd = { row, col };
    this.setState({ selecting: false, selEnd });

    // Normalise
    const r0 = Math.min(selStart.row, selEnd.row);
    const r1 = Math.max(selStart.row, selEnd.row);
    const c0 = Math.min(selStart.col, selEnd.col);
    const c1 = Math.max(selStart.col, selEnd.col);

    // If single click on a seat → toggle enabled (not for stage zones)
      if (r0 === r1 && c0 === c1) {
      const zone = this.findZoneForCell(r0, c0);
      if (zone && zone.isStage) {
        // Click on stage zone → open editor
        this.openEditZone(zone);
        this.setState({ selStart: null, selEnd: null });
        return;
      }
      const seat = this.findSeat(r0, c0, zone);
      if (seat) {
        this.toggleSeat(seat);
        return;
      }
    }

    // Multi-cell selection → open new zone modal
    this.setState({
      zoneModalOpen: true,
      zoneModalData: null,
      zoneModalSelStart: { row: r0, col: c0 },
      zoneModalSelEnd: { row: r1, col: c1 },
      selStart: null,
      selEnd: null,
    });
  };

  // ─── seat toggle ──────────────────────────────────────────────────────────

  toggleSeat = (seat) => {
    const dto = { idSeat: seat.idSeat, enabled: !seat.enabled };
    doFetchJson("TEAN", "updateSeat", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(dto) })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          this.updateSeatInState(seat.idSeat, { enabled: !seat.enabled });
        } else {
          toast.show({ message: (json && json.MESSAGE) || "Error al actualizar butaca", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }));
  };

  updateSeatInState = (idSeat, patch) => {
    this.setState((prev) => {
      if (!prev.venue) return {};
      const zones = prev.venue.zones.map((z) => ({
        ...z,
        seats: (z.seats || []).map((s) => s.idSeat === idSeat ? { ...s, ...patch } : s),
      }));
      return { venue: { ...prev.venue, zones } };
    });
  };

  // ─── zone CRUD ────────────────────────────────────────────────────────────

  openEditZone = (zone) => {
    this.setState({ zoneModalOpen: true, zoneModalData: zone, zoneModalSelStart: null, zoneModalSelEnd: null });
  };

  closeZoneModal = () => {
    this.setState({ zoneModalOpen: false, zoneModalData: null, zoneModalSelStart: null, zoneModalSelEnd: null });
  };

  onZoneSaved = () => {
    this.closeZoneModal();
    this.reloadVenue();
  };

  askDeleteZone = (zone) => this.setState({ confirmDeleteZone: zone });
  cancelDeleteZone = () => this.setState({ confirmDeleteZone: null });

  confirmDeleteZoneNow = () => {
    const { confirmDeleteZone } = this.state;
    if (!confirmDeleteZone) return;
    doFetchJson("TEAN", "deleteZone/" + confirmDeleteZone.idZone, { method: "DELETE", headers: { "Content-Type": "application/json" } })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: "Zona eliminada", type: "success" });
          this.setState({ confirmDeleteZone: null });
          this.reloadVenue();
        } else {
          toast.show({ message: (json && json.MESSAGE) || "Error al eliminar la zona", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }));
  };

  // ─── reload ───────────────────────────────────────────────────────────────

  reloadVenue = () => {
    const { venue } = this.state;
    if (!venue) return;
    this.setState({ loading: true });
    doFetchJson("TEAN", "getVenue/" + venue.idVenue)
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          this.setState({ venue: json.DATA });
          if (this.props.onVenueChanged) this.props.onVenueChanged(json.DATA);
        }
      })
      .catch(() => toast.show({ message: "Error al recargar la sala", type: "danger" }))
      .finally(() => this.setState({ loading: false }));
  };

  // ─── render ───────────────────────────────────────────────────────────────

  renderGrid() {
    const { venue, selecting } = this.state;
    if (!venue) return null;
    const { gridRows, gridCols } = venue;

    const rows = [];
    for (let r = 0; r < gridRows; r++) {
      const cells = [];
      for (let c = 0; c < gridCols; c++) {
        const zone = this.findZoneForCell(r, c);
        const seat = this.findSeat(r, c, zone);
        const inSel = this.isInSelection(r, c);

        // ── Stage zone: render as solid block, only label on center cell
        if (zone && zone.isStage) {
          const bg = this.hexToRgba(zone.color || "#374151", 0.85);
          const isCenterRow = r === Math.floor((zone.rowStart + zone.rowEnd) / 2);
          const isCenterCol = c === Math.floor((zone.colStart + zone.colEnd) / 2);
          cells.push(
            <div
              key={`${r}-${c}`}
              className="venue-grid-cell venue-grid-cell--stage"
              style={{ backgroundColor: bg }}
              title={`Escenario: ${zone.name} (clic para editar)`}
              onMouseDown={(e) => this.handleCellMouseDown(r, c, e)}
              onMouseEnter={() => this.handleCellMouseEnter(r, c)}
              onMouseUp={() => this.handleCellMouseUp(r, c)}
            >
              {isCenterRow && isCenterCol && (
                <span className="venue-grid-cell-stage-label">{zone.name}</span>
              )}
            </div>
          );
          continue;
        }

        let bg = "#f0f0f0";
        let opacity = 1;
        let title = `(${r},${c})`;
        let seatLabel = null;

        if (zone) {
          bg = this.hexToRgba(zone.color || "#3b82f6", 0.35);
          if (seat) {
            if (!seat.enabled) opacity = 0.25;
            seatLabel = seat.seatNumber;
            title = `${zone.name} — ${seat.seatNumber || `F${r} C${c}`}${seat.enabled ? "" : " (inhab.)"}`;
          }
        }

        if (inSel) bg = "rgba(99,102,241,0.5)";

        cells.push(
          <div
            key={`${r}-${c}`}
            className={`venue-grid-cell${seat ? " venue-grid-cell--seat" : ""}${inSel ? " venue-grid-cell--sel" : ""}`}
            style={{ backgroundColor: bg, opacity }}
            title={title}
            onMouseDown={(e) => this.handleCellMouseDown(r, c, e)}
            onMouseEnter={() => this.handleCellMouseEnter(r, c)}
            onMouseUp={() => this.handleCellMouseUp(r, c)}
          >
            {seatLabel && <span className="venue-grid-cell-label">{seatLabel}</span>}
          </div>
        );
      }
      rows.push(
        <div key={r} className="venue-grid-row">
          {cells}
        </div>
      );
    }

    return (
      <div
        className={`venue-grid${selecting ? " venue-grid--selecting" : ""}`}
        style={{ "--cols": gridCols }}
        ref={this.gridRef}
        onMouseLeave={() => {
          if (this.state.selecting) this.setState({ selecting: false });
        }}
      >
        {rows}
      </div>
    );
  }

  renderZoneList() {
    const { venue } = this.state;
    if (!venue || !Array.isArray(venue.zones) || venue.zones.length === 0) {
      return (
        <div className="text-muted fst-italic small mt-2">
          Sin zonas. Selecciona celdas en la cuadrícula para crear una zona.
        </div>
      );
    }
    return (
      <div className="mt-3">
        <div className="fw-semibold mb-2 small">Zonas ({venue.zones.length})</div>
        <div className="d-flex flex-wrap gap-2">
          {venue.zones.map((z) => (
            <div key={z.idZone} className="venue-zone-chip" style={{ borderColor: z.color || "#ccc" }}>
              <span
                className="venue-zone-chip-color"
                style={{ backgroundColor: z.color || "#ccc" }}
              />
              <span className="venue-zone-chip-name">{z.name}</span>
              {z.isStage ? (
                <span className="badge bg-dark ms-1" title="Zona escenario">
                  <MDBIcon fas icon="theater-masks" size="xs" />
                </span>
              ) : (
              <span className="text-muted small mx-1">
                ({(z.seats || []).filter((s) => s.enabled).length}/{(z.seats || []).length})
              </span>
              )}
              <button
                className="btn btn-sm btn-link p-0 ms-1 text-secondary"
                onClick={() => this.openEditZone(z)}
                title="Editar zona"
              >
                <MDBIcon fas icon="pen" size="xs" />
              </button>
              <button
                className="btn btn-sm btn-link p-0 ms-1 text-danger"
                onClick={() => this.askDeleteZone(z)}
                title="Eliminar zona"
              >
                <MDBIcon fas icon="trash" size="xs" />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  render() {
    const { venue, loading, zoneModalOpen, zoneModalData, zoneModalSelStart, zoneModalSelEnd,
      confirmDeleteZone } = this.state;

    if (loading) {
      return (
        <div className="text-center py-4">
          <MDBSpinner />
        </div>
      );
    }
    if (!venue) return <div className="text-muted fst-italic">Sin sala seleccionada.</div>;

    return (
      <div className="venue-editor">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <span className="fw-semibold">{venue.name}</span>
            <span className="text-muted small ms-2">
              {venue.gridRows} × {venue.gridCols} ({venue.gridRows * venue.gridCols} celdas)
            </span>
          </div>
          <div className="d-flex gap-2">
            <MDBBtn size="sm" color="light" onClick={this.reloadVenue} title="Recargar">
              <MDBIcon fas icon="sync-alt" />
            </MDBBtn>
            <MDBBtn
              size="sm"
              color="primary"
              onClick={() => this.setState({ zoneModalOpen: true, zoneModalData: null, zoneModalSelStart: null, zoneModalSelEnd: null })}
            >
              <MDBIcon fas icon="plus" className="me-1" />
              Nueva zona
            </MDBBtn>
          </div>
        </div>

        {/* Leyenda */}
        <div className="venue-editor-legend mb-2">
          <span className="venue-legend-item">
            <span className="venue-legend-dot" style={{ background: "#f0f0f0", border: "1px solid #ccc" }} />
            Sin zona
          </span>
          <span className="venue-legend-item ms-3">
            <span className="venue-legend-dot" style={{ background: "rgba(99,102,241,0.5)" }} />
            Selección activa
          </span>
          <span className="text-muted small ms-3">
            Clic = toggle habilitado. Drag = nueva zona.
          </span>
        </div>

        {/* Grid */}
        <div className="venue-editor-grid-wrapper">
          {this.renderGrid()}
        </div>

        {/* Zone list */}
        {this.renderZoneList()}

        {/* ZoneEditorModal */}
        <ZoneEditorModal
          show={zoneModalOpen}
          zone={zoneModalData}
          idVenue={venue.idVenue}
          gridRows={venue.gridRows}
          gridCols={venue.gridCols}
          selectionStart={zoneModalSelStart}
          selectionEnd={zoneModalSelEnd}
          onClose={this.closeZoneModal}
          onSaved={this.onZoneSaved}
        />

        {/* Confirm delete zone */}
        {confirmDeleteZone && (
          <div className="venue-confirm-overlay">
            <div className="venue-confirm-box">
              <div className="fw-semibold mb-2">
                <MDBIcon fas icon="exclamation-triangle" className="text-warning me-2" />
                ¿Eliminar zona "{confirmDeleteZone.name}"?
              </div>
              <div className="text-muted small mb-3">
                Se eliminarán todas las butacas de la zona. Las entradas vinculadas mantendrán su ID de butaca como referencia histórica.
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <MDBBtn color="secondary" outline size="sm" onClick={this.cancelDeleteZone}>
                  Cancelar
                </MDBBtn>
                <MDBBtn color="danger" size="sm" onClick={this.confirmDeleteZoneNow}>
                  <MDBIcon fas icon="trash" className="me-1" />
                  Eliminar
                </MDBBtn>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default VenueEditor;
